import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Video as VideoIcon, Share2 } from 'lucide-react';
import VideoPlayerModal from '../components/VideoPlayerModal';
import ShareSheet from '../components/ShareSheet';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * VideosPage — public library of published `video` content.
 * Videos are database-first (Prompt #5 rule). If no published videos
 * exist yet, we show a calm empty state rather than fabricating cards.
 * Tapping a card opens the shared VideoPlayerModal, which pauses any
 * active audio so two media sources never play at once.
 */
const VideosPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const shareFor = (v) => ({
    kind: 'video',
    id: v.id,
    title: v.title,
    text: `"${v.title}" — watch with Rowdy on Reflections by Rowdy`,
    url: `${window.location.origin}/videos`,
    recommendable: true,
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/content?type=video&limit=50`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        if (cancelled) return;
        setItems((data.items || []).filter((c) => c.videoAssetId || c.videoUrl));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="pb-32 min-h-screen bg-[#F6F1E7]" data-testid="videos-page">
      <div className="px-5 pt-5 pb-3">
        <button
          onClick={() => navigate('/more')}
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-700 mb-3"
          data-testid="videos-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="rbr-eyebrow mb-2" style={{ color: '#C79A4A' }}>Videos</p>
        <h1
          className="text-3xl leading-tight text-stone-800 mb-1"
          style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
        >
          Watch with Rowdy.
        </h1>
        <p className="text-sm text-stone-500">
          Long-form reflections, guided practices, and moments meant to be seen as well as heard.
        </p>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <p className="text-center py-10 text-stone-500">Loading videos…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-14 px-6" data-testid="videos-empty">
            <VideoIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
              New videos are on the way.
            </p>
            <p className="text-[12px] text-stone-500 mt-1">Rowdy is preparing something to share.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((v) => (
              <button
                key={v.id}
                onClick={() => setSelected(v)}
                data-testid={`video-card-${v.slug || v.id}`}
                className="relative rounded-2xl overflow-hidden text-left group active:scale-[0.99] transition-transform"
                style={{ minHeight: 200 }}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${v.posterImageUrl || v.coverImageUrl || ''})` }}
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setShareItem(shareFor(v)); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShareItem(shareFor(v)); } }}
                  data-testid={`video-share-${v.slug || v.id}`}
                  aria-label="Share"
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(13,43,36,0.45)', color: '#F6F1E7', backdropFilter: 'blur(6px)' }}
                >
                  <Share2 className="w-4 h-4" />
                </span>
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(13,43,36,0.10) 0%, rgba(13,43,36,0.85) 100%)' }}
                />
                <div className="relative h-full flex flex-col justify-end p-4 min-h-[200px]" style={{ color: '#F6F1E7' }}>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>
                    {v.category || 'Video'}
                  </p>
                  <p
                    className="text-xl leading-snug mt-0.5"
                    style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
                  >
                    {v.title}
                  </p>
                  {v.shortDescription && (
                    <p className="text-[11px] mt-1 line-clamp-2" style={{ color: 'rgba(246,241,231,0.75)' }}>
                      {v.shortDescription}
                    </p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                      style={{ background: 'rgba(199,154,74,0.22)', color: '#C79A4A', border: '1px solid rgba(199,154,74,0.4)' }}
                    >
                      <VideoIcon className="w-3 h-3" /> {v.durationSeconds ? `${Math.round(v.durationSeconds / 60)} min` : 'Watch'}
                    </span>
                    <span
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(246,241,231,0.15)', color: '#F6F1E7', backdropFilter: 'blur(6px)' }}
                      aria-hidden
                    >
                      <Play className="w-5 h-5 ml-0.5" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <ShareSheet open={!!shareItem} onClose={() => setShareItem(null)} content={shareItem} />
      <VideoPlayerModal
        open={!!selected}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default VideosPage;
